/**
 * Timeline — vertical list of attestations, newest first. Each entry
 * carries two selection affordances (A/B) so the diff panel has two
 * entries to compare.
 */

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { HashDisplay } from '@/components/HashDisplay';
import type { AttestationRecordFull } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatAbsoluteTime, formatRelativeTime } from '@/utils/format';
import { cn } from '@/utils/cn';

import { WitnessChip } from './WitnessChip';

export function Timeline({
  entries,
  pickA,
  pickB,
  onPick,
}: {
  entries: AttestationRecordFull[];
  pickA: string | null;
  pickB: string | null;
  onPick: (slot: 'A' | 'B', id: string) => void;
}) {
  const { t } = useTranslation('attestations');
  const locale = useLocaleStore((s) => s.locale);

  return (
    <ol className="flex flex-col">
      {entries.map((a, i) => {
        const selectedSlot = pickA === a.id ? 'A' : pickB === a.id ? 'B' : null;
        const isLast = i === entries.length - 1;
        return (
          <li key={a.id} className="relative flex items-start gap-4 py-4">
            {!isLast ? (
              <span
                aria-hidden="true"
                className="absolute start-[15px] top-12 bottom-0 w-px bg-border"
              />
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-surface text-xs font-semibold',
                selectedSlot
                  ? 'border-spark bg-spark-soft text-spark'
                  : 'border-border text-ink-muted',
              )}
            >
              {selectedSlot ?? entries.length - i}
            </span>

            <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-lg border border-border bg-surface p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-col">
                  <span className="font-mono text-xs font-medium text-ink">
                    {a.execution_label}
                  </span>
                  <span
                    className="text-[11px] text-ink-subtle"
                    title={formatAbsoluteTime(a.signed_at.iso, locale)}
                  >
                    {formatRelativeTime(a.signed_at.iso, locale)} ·{' '}
                    {formatAbsoluteTime(a.signed_at.iso, locale)}
                  </span>
                </div>
                <WitnessChip status={a.witness_status} />
              </div>

              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                <Row label="input_hash"><HashDisplay value={a.input_hash} /></Row>
                <Row label="output_hash"><HashDisplay value={a.output_hash} /></Row>
                <Row label="wasm_hash"><HashDisplay value={a.wasm_hash} /></Row>
                <Row label={t('entry.fuel')}>
                  <span className="font-mono">{a.fuel_consumed.toLocaleString(locale)}</span>
                </Row>
                <Row label={t('entry.bytes_in')}>
                  <span className="font-mono">{a.bytes_in.toLocaleString(locale)}</span>
                </Row>
                <Row label={t('entry.bytes_out')}>
                  <span className="font-mono">{a.bytes_out.toLocaleString(locale)}</span>
                </Row>
                <Row label="engine_signature"><HashDisplay value={a.engine_signature} /></Row>
                <Row label="wasmtime">
                  <span className="font-mono">{a.wasmtime_version}</span>
                </Row>
              </dl>

              <div className="mt-1 flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant={pickA === a.id ? 'primary' : 'ghost'}
                  onClick={() => onPick('A', a.id)}
                >
                  {pickA === a.id ? t('entry.selected_a') : t('entry.select_a')}
                </Button>
                <Button
                  size="sm"
                  variant={pickB === a.id ? 'primary' : 'ghost'}
                  onClick={() => onPick('B', a.id)}
                >
                  {pickB === a.id ? t('entry.selected_b') : t('entry.select_b')}
                </Button>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <dt className="w-28 shrink-0 text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}
